/**
 * Perler animal patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const perlerAnimalPatternsPack: ToilePack = {
    slug: 'perler-animal-patterns',
    category: 'craft',
    name: 'Perler animal patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'pets', label: 'Pets', subject: 'a perler bead pattern of a sitting cat in orange and white on a square pegboard grid, each bead a coloured circle and the empty pegs shown as faint rings'},
        {key: 'farm', label: 'Farm animals', subject: 'a perler bead pattern of four small farm animals, a pig, a cow, a sheep and a chick, arranged on one square pegboard, each about fifteen beads tall'},
        {key: 'sea', label: 'Sea animals', subject: 'a perler bead pattern of a turtle, a whale and a small octopus on one pegboard grid, blues and greens with black beads for the eyes'},
    ],
    styles: [
        {key: 'colour', label: 'Colour chart', style: 'a clean pegboard pattern, every bead a flat coloured circle with a small round hole at its centre, empty pegs shown as faint rings', palette: 'bright fuse bead colours on a white ground', framing: 'the pegboard centred on a plain white sheet with an even margin'},
        {key: 'symbol', label: 'Symbol chart', style: 'a pegboard pattern for black-and-white printing, every bead a pale circle carrying one small black symbol so each shade stays distinct', palette: 'pale colours kept under crisp black symbols', framing: 'the pegboard filling most of a white sheet with a narrow clean margin'},
        {key: 'graph', label: 'Graph paper', style: 'a hand-coloured worksheet, circles filled with felt-tip pens, slightly uneven strokes, on blue squared graph paper', palette: 'bright felt-tip colours on pale blue grid paper', framing: 'the pegboard drawn on a plain sheet with a small margin all round'},
    ],
    directions: ['a dog', 'a fox', 'a panda'],
    directionHint: 'try: a dog',
    seo: {
        title: 'Perler animal patterns: pets, farm and sea life',
        metaDescription: 'Nine perler bead animal patterns: pets, farm animals and sea creatures on a pegboard grid, in three styles.',
        h1: 'Perler animal patterns: pets, farm animals and sea creatures',
        tagline: 'Bead patterns on a square pegboard, each bead a coloured circle.',
        intro: 'Fuse beads are placed on a pegboard and ironed together, and animals are what people make most. A pattern shows each bead as a coloured circle, with empty pegs as faint rings. These nine cover a cat, four farm animals and three sea creatures. Each comes as a colour pegboard, a black-and-white symbol pegboard, or a hand-coloured sheet. Type a different animal and the same pegboard carries it.',
        useCases: [
            'a keyring or magnet',
            'a classroom craft',
            'a birthday activity for children',
            'a wall display of animals',
        ],
        faq: [
            {q: 'What does an empty ring mean?', a: 'An empty ring is a peg with no bead, so it stays blank in the finished design.'},
            {q: 'Is the grid square?', a: 'Yes. Each pattern sits on a square pegboard grid.'},
            {q: 'Which style prints in black and white?', a: 'The symbol pegboard, where each bead carries a black symbol.'},
        ],
    },
}
